// GLSL chunks for the water planes (TerrainWater). Two looks:
//
//  * sea (ocean + coast tiles): colour, shallows, shelf and foam are driven by the shore field texture
//    (shore/shoreField.ts), a WORLD-space map of the distance to land, so the water reads as one continuous sea:
//    nothing is tile-local any more, which is what used to draw every hex of the water. A smooth turquoise ->
//    deep blue gradient, a sandy seabed shelf showing through the shallows, an animated foam line that follows
//    the shoreline (the beach ramp's waterline or the cliff base) and faint wave sets rolling in.
//  * river: the per-edge channel + bank drawing (unchanged), tile-local on purpose.
//
// All GLSL is plain ES 3.00 that three's MeshBasicMaterial already compiles under; every varying is declared in
// both stages and there are no extensions.

import * as THREE from 'three';
import { SKY_HORIZON, SKY_ZENITH } from './sceneRenderConfig.js';
import { seaGradientGLSL } from './shore/waterPalette.js';
import { GLSL_UNPACK_RGB } from './terrainEdges.js';
import { WATER_SKIRT_DEPTH } from './waterSkirt.js';

const HEX_SIZE = 1;
/** Hex apothem of the (radius 1) water plane: centre to edge midpoint. */
const WATER_APOTHEM = HEX_SIZE * Math.cos(Math.PI / 6);
/** Half-width of a river channel, world units. */
const RIVER_HALF_WIDTH = 0.24;

const glslFloat = (v: number) => v.toFixed(5);

/**
 * Sun state the water shades against. DayNightLighting (or any scene that only
 * sets `dayNightState.sunHeight`) drives it through WaterAnimator, so the sea
 * glints and reflects the sky in every scene, debug dioramas included.
 */
export const waterLight = {
  dir: { value: new THREE.Vector3(0.35, 0.85, 0.25).normalize() },
  color: { value: new THREE.Color(1, 1, 1) },
  intensity: { value: 1.2 },
};

/** Near-flat surface normal facing the sky, for a still sea / reduced motion. */
const SKY_ZENITH_GLSL = (() => {
  const c = new THREE.Color(SKY_ZENITH);
  return `vec3(${glslFloat(c.r)}, ${glslFloat(c.g)}, ${glslFloat(c.b)})`;
})();
const SKY_HORIZON_GLSL = (() => {
  const c = new THREE.Color(SKY_HORIZON);
  return `vec3(${glslFloat(c.r)}, ${glslFloat(c.g)}, ${glslFloat(c.b)})`;
})();
const glslVec3 = (hex: string) => {
  const c = new THREE.Color(hex);
  return `vec3(${glslFloat(c.r)}, ${glslFloat(c.g)}, ${glslFloat(c.b)})`;
};

export interface WaterChunks {
  readonly vertexPars: string;
  readonly vertexMain: string;
  readonly fragmentPars: string;
  readonly fragmentMain: string;
}

/** Fog-of-war tone, shared by both looks: the same factors the CPU applies to instance colours. */
const FOG_TONE_GLSL = /* glsl */ `
    vec3 _fogTone(vec3 c) {
      float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
      return mix(vec3(l), c, _vTone.y) * _vTone.x;
    }`;

/** Sea water: world-space shore-field shading. */
export function seaWaterShader(): WaterChunks {
  const vertexPars = /* glsl */ `
    attribute vec3 aTone;
    varying vec2 _vTone;
    varying float _vSk;
    varying vec3 _vWorld;`;
  const vertexMain = /* glsl */ `
    _vTone = aTone.xy;
    // 0 on the water surface, 1 at the bottom of the skirt that closes steps between depth classes.
    _vSk = clamp(-position.y / ${glslFloat(WATER_SKIRT_DEPTH)}, 0.0, 1.0);
    // World position for the view vector (the wave displacement is added later,
    // in TerrainWater's own chunk — a few centimetres, irrelevant here).
    _vWorld = (instanceMatrix * vec4(transformed, 1.0)).xyz;`;
  const fragmentPars = /* glsl */ `
    uniform vec3 uSunDir;
    uniform vec3 uSunColor;
    uniform float uSunInt;
    // Vertex-wave amplitude of this depth class, for the analytic normal.
    uniform float uWaveAmp;
    varying vec3 _vWorld;
    uniform sampler2D uShoreTex;
    uniform vec2 uShoreOrg;
    uniform vec2 uShoreInv;
    varying vec2 _vTone;
    varying float _vSk;
    float _vhash(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }
    float _vnoise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(mix(_vhash(i), _vhash(i + vec2(1.0, 0.0)), u.x),
                 mix(_vhash(i + vec2(0.0, 1.0)), _vhash(i + vec2(1.0, 1.0)), u.x), u.y);
    }
    ${FOG_TONE_GLSL}
    ${seaGradientGLSL()}`;
  const fragmentMain = /* glsl */ `
    {
      vec2 _uv = (vWorldXZ - uShoreOrg) * uShoreInv;
      vec4 _sf = texture2D(uShoreTex, _uv);
      float _d = _sf.r * 3.0;   // distance to land
      float _wl = _sf.g;        // where the shoreline meets the water, outward from the hex edge
      float _cl = _sf.b;        // cliff share of that shoreline
      // Slow world-space wobble so no contour is ruler-straight or follows the hex lattice.
      float _n1 = sin(vWorldXZ.x * 2.3 + uTime * 0.21) * sin(vWorldXZ.y * 2.0 - uTime * 0.17);
      float _n2 = 0.5 * sin(vWorldXZ.x * 5.1 - uTime * 0.4 + vWorldXZ.y * 3.3);
      float _dw = max(_d + 0.035 * _n1 + 0.015 * _n2, 0.0);
      vec3 _col = _seaGrad(_dw);
      // The sandy seabed shelf shows through the shallows (beaches only, cliffs drop away).
      float _shelf = (1.0 - smoothstep(_wl, _wl + 0.5, _dw)) * (1.0 - _cl);
      _col = mix(_col, _SHELF, _shelf * 0.5);
      // Foam: a line at the waterline that breathes in and out, plus faint wave sets running in.
      float _rel = _dw - _wl;
      float _swash = 0.03 + 0.04 * sin(uTime * 0.9 + vWorldXZ.x * 1.7 + vWorldXZ.y * 1.3);
      float _line = 1.0 - smoothstep(0.0, 0.05 + 0.03 * _cl, abs(_rel - _swash));
      float _sets = smoothstep(0.8, 1.0, sin(_rel * 16.0 + uTime * 1.5 + _n1 * 2.0))
                  * (1.0 - smoothstep(0.05, 0.8, _rel)) * step(0.0, _rel);
      float _foam = clamp(_line * 0.85 + _sets * 0.28 * (1.0 - _cl), 0.0, 1.0)
                  * step(0.001, _wl) * (1.0 - smoothstep(0.9, 1.4, _d));
      _col = mix(_col, _FOAM, _foam);
      // A skirt face (a step between water classes) fades to the deep colour instead of streaking the rim colour.
      _col = mix(_col, _seaGrad(3.0), _vSk * 0.85);

      // --- Light on the surface -----------------------------------------------
      // The vertex stage displaces the sheet with three sines; differentiating
      // that same sum analytically gives a normal without any extra geometry.
      float _t = uTime;
      float _c1 = cos(vWorldXZ.x * 2.5 + _t);
      float _s1 = sin(vWorldXZ.x * 2.5 + _t);
      float _c2 = cos(vWorldXZ.y * 2.5 + _t * 0.7);
      float _s2 = sin(vWorldXZ.y * 2.5 + _t * 0.7);
      float _c3 = cos(vWorldXZ.x * 5.0 + _t * 1.3);
      float _s3 = sin(vWorldXZ.x * 5.0 + _t * 1.3);
      float _c4 = cos(vWorldXZ.y * 5.0 - _t * 0.9);
      float _s4 = sin(vWorldXZ.y * 5.0 - _t * 0.9);
      float _c5 = cos((vWorldXZ.x + vWorldXZ.y) * 1.1 + _t * 0.5);
      float _s5 = sin((vWorldXZ.x + vWorldXZ.y) * 1.1 + _t * 0.5);
      float _dhx = 2.5 * _c1 * _c2 + 3.0 * _c3 * _s4 + 0.385 * _c5;
      float _dhz = -2.5 * _s1 * _s2 + 3.0 * _s3 * _c4 + 0.385 * _c5;
      vec3 _N = normalize(vec3(-_dhx * uWaveAmp, 1.0, -_dhz * uWaveAmp));
      vec3 _V = normalize(cameraPosition - _vWorld);
      vec3 _L = normalize(uSunDir);
      float _ndv = clamp(dot(_N, _V), 0.0, 1.0);
      // Schlick fresnel (water F0 = 0.02) toward a sky gradient: the sea mirrors
      // the sky at grazing angles and stays blue underfoot, which is what makes
      // water read as water rather than as a coloured floor.
      vec3 _R = reflect(-_V, _N);
      vec3 _sky = mix(${SKY_HORIZON_GLSL}, ${SKY_ZENITH_GLSL}, pow(clamp(_R.y, 0.0, 1.0), 0.55));
      float _fres = 0.02 + 0.98 * pow(1.0 - _ndv, 5.0);
      _col = mix(_col, _sky, clamp(_fres, 0.0, 1.0) * 0.6);
      // Sun glint: a tight sparkle plus a broad sheen, fading out with the sun.
      vec3 _H = normalize(_L + _V);
      float _ndh = max(dot(_N, _H), 0.0);
      float _spec = pow(_ndh, 320.0) * 6.0 + pow(_ndh, 60.0) * 0.12;
      _col += uSunColor * (uSunInt * _spec) * smoothstep(-0.05, 0.1, _L.y);

      // Crest foam: the same wave sum the normal came from, so whitecaps break
      // on the crests that actually rise (and on nothing else). Value noise
      // tears the white into lace so it reads as foam, not as a painted line.
      float _wave = _s1 * _c2 + 0.6 * _s3 * _s4 + 0.35 * _s5;
      float _crest = smoothstep(0.82, 1.15, _wave);
      float _lace = _vnoise(vWorldXZ * 3.4 + vec2(_t * 0.5, -_t * 0.3)) * 0.65
                  + _vnoise(vWorldXZ * 7.9 - vec2(_t * 0.35, _t * 0.2)) * 0.35;
      // No distance falloff: crests break everywhere, and the fog already
      // softens the far sea (distance-to-land killed this before it was visible).
      float _cap = _crest * smoothstep(0.55, 0.86, _lace);
      _col = mix(_col, _FOAM, _cap * 0.34);

      diffuseColor.rgb = _fogTone(_col);
    }`;
  return { vertexPars, vertexMain, fragmentPars, fragmentMain };
}

/**
 * River water: a channel from the centre to each edge that faces more water, with the rest of the hex drawn as
 * river bank in the land neighbours' colours (the per-edge shoreline drawing that used to cover every class).
 */
export function riverWaterShader(): WaterChunks {
  const vertexPars = /* glsl */ `
    attribute vec4 aEdgeMask;
    attribute vec3 aNbA;
    attribute vec3 aNbB;
    attribute vec3 aTone;
    varying vec2 _vLocalXZ;
    varying vec2 _vTone;
    varying vec3 _vBank;
    flat varying ivec4 _vMask;
    ${GLSL_UNPACK_RGB}`;
  const vertexMain = /* glsl */ `
    _vLocalXZ = position.xz;
    _vTone = aTone.xy;
    _vMask = ivec4(aEdgeMask + 0.5);
    {
      // River bank: the average colour of the land neighbours.
      vec3 _sum = vec3(0.0);
      float _n = 0.0;
      float _nb[6] = float[6](aNbA.x, aNbA.y, aNbA.z, aNbB.x, aNbB.y, aNbB.z);
      for (int k = 0; k < 6; k++) {
        if ((_vMask.z & (1 << k)) != 0) { _sum += _unpackRGB(_nb[k]); _n += 1.0; }
      }
      _vBank = _n > 0.0 ? _sum / _n : ${glslVec3('#7d9a4c')};
    }`;
  const fragmentPars = /* glsl */ `
    varying vec2 _vLocalXZ;
    varying vec2 _vTone;
    varying vec3 _vBank;
    flat varying ivec4 _vMask;
    ${FOG_TONE_GLSL}`;
  const fragmentMain = /* glsl */ `
    {
      vec2 _p = _vLocalXZ;
      float _chan = 1e3;
      int _wet = (_vMask.x & 63) | _vMask.y;
      for (int k = 0; k < 6; k++) {
        float _a = radians(60.0 + 60.0 * float(k));
        vec2 _n = vec2(cos(_a), sin(_a));
        int _bit = 1 << k;
        if ((_wet & _bit) != 0) {
          // Distance to the channel skeleton: centre -> this edge's midpoint.
          float _t = clamp(dot(_p, _n), 0.0, ${glslFloat(WATER_APOTHEM + 0.02)});
          _chan = min(_chan, length(_p - _n * _t));
        }
      }
      // An isolated river tile (no water neighbours) becomes a small pond.
      if (_wet == 0) _chan = length(_p) - 0.1;
      float _meander = 0.025 * sin(vWorldXZ.x * 3.1 + vWorldXZ.y * 2.3);
      float _c = _chan + _meander;
      float _inWater = 1.0 - smoothstep(${glslFloat(RIVER_HALF_WIDTH - 0.02)}, ${glslFloat(RIVER_HALF_WIDTH + 0.015)}, _c);
      vec3 _bank = _fogTone(_vBank);
      // Darker damp earth along the banks.
      _bank = mix(_bank, _bank * vec3(0.62, 0.58, 0.5), 1.0 - smoothstep(${glslFloat(RIVER_HALF_WIDTH)}, ${glslFloat(RIVER_HALF_WIDTH + 0.12)}, _c));
      float _lip = 1.0 - smoothstep(0.0, 0.03, abs(_c - ${glslFloat(RIVER_HALF_WIDTH - 0.03)}));
      vec3 _water = mix(diffuseColor.rgb, _fogTone(vec3(0.55, 0.75, 0.78)), _lip * 0.35);
      diffuseColor.rgb = mix(_bank, _water, _inWater);
      _bankMask = 1.0 - _inWater;
    }`;
  return { vertexPars, vertexMain, fragmentPars, fragmentMain };
}
